import { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, MapPin, Phone, Mail, Instagram } from "lucide-react";
import { toast } from "sonner";
import { api, WHATSAPP_URL, COMPANY } from "@/api";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Mesajınız alındı. En kısa sürede dönüş yapacağız.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Mesaj gönderilemedi. Lütfen tekrar deneyin.");
    } finally {
      setSending(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <section id="iletisim" className="py-24 md:py-36 bg-beige-soft" data-testid="contact-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="text-xs font-semibold tracking-[0.25em] uppercase text-stone-500 mb-4">İletişim</p>
          <h2 className="font-serif font-light tracking-tight text-stone-800 text-3xl sm:text-4xl lg:text-5xl leading-tight">
            Projenizi birlikte <em className="text-wood-light">tasarlayalım</em>
          </h2>
          <p className="text-stone-600 font-light leading-relaxed mt-6 max-w-md">
            Fiyat bilgisi, özel ölçü siparişleri ve showroom randevusu için bize yazın; bir gün içinde dönüş yapalım.
          </p>
          <div className="mt-10 flex flex-col gap-5 text-stone-700">
            <span className="flex items-start gap-4 text-sm font-light"><MapPin size={16} className="text-wood-light mt-0.5 shrink-0" /> {COMPANY.address}</span>
            {COMPANY.phones.map((p) => (
              <a key={p} href={`tel:+90${p.replace(/\s/g, "")}`} className="flex items-center gap-4 text-sm font-light hover:text-stone-900 transition-colors duration-300" data-testid={`contact-phone-${p.replace(/\s/g, "-")}`}>
                <Phone size={16} className="text-wood-light shrink-0" /> {p}
              </a>
            ))}
            <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-4 text-sm font-light hover:text-stone-900 transition-colors duration-300" data-testid="contact-email-link">
              <Mail size={16} className="text-wood-light shrink-0" /> {COMPANY.email}
            </a>
            <a href={`https://instagram.com/${COMPANY.instagram}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 text-sm font-light hover:text-stone-900 transition-colors duration-300" data-testid="contact-instagram-link">
              <Instagram size={16} className="text-wood-light shrink-0" /> @{COMPANY.instagram}
            </a>
          </div>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-whatsapp-button"
            className="mt-10 inline-flex items-center gap-3 bg-stone-900 text-stone-50 px-7 py-4 text-xs font-semibold tracking-[0.2em] uppercase hover:bg-wood transition-colors duration-300"
          >
            <MessageCircle size={15} /> WhatsApp'tan Yazın
          </a>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col gap-8 md:pt-16"
          data-testid="contact-form"
        >
          <input required placeholder="Adınız Soyadınız" value={form.name} onChange={set("name")} className="field-line" data-testid="contact-name-input" />
          <input required type="email" placeholder="E-posta adresiniz" value={form.email} onChange={set("email")} className="field-line" data-testid="contact-email-input" />
          <input placeholder="Telefon (opsiyonel)" value={form.phone} onChange={set("phone")} className="field-line" data-testid="contact-phone-input" />
          <textarea required rows={4} placeholder="Mesajınız" value={form.message} onChange={set("message")} className="field-line resize-none" data-testid="contact-message-input" />
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit-button"
            className="self-start bg-stone-900 text-stone-50 px-10 py-4 text-xs font-semibold tracking-[0.2em] uppercase hover:bg-wood transition-colors duration-300 disabled:opacity-50"
          >
            {sending ? "Gönderiliyor..." : "Mesajı Gönder"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
